/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useCallback } from 'react';

// -----------------------------------------------
// useTaskLayoutMode — single source of truth for the user's preferred Tasks V2
// layout (Modal | Full screen | Sidebar). Persists choice to localStorage so
// the next slideout/modal opens in the previously chosen mode. Default for
// first-time users is `sidebar` (matches the historical behaviour of the
// TaskSlideout).
//
// Usage:
//   const { mode, setMode } = useTaskLayoutMode();
//   if (mode === 'modal') ...
//
// Storage key: tasks-v2/layout. Allowed values: 'sidebar' | 'modal' | 'full'.
// Cross-tab updates are picked up via the `storage` event so opening two tabs
// and switching layout in one updates the other on its next render.
// -----------------------------------------------

export const TASK_LAYOUT_MODES = {
    SIDEBAR: 'sidebar',
    MODAL: 'modal',
    FULL: 'full',
};

const STORAGE_KEY = 'tasks-v2/layout';
const DEFAULT_MODE = TASK_LAYOUT_MODES.SIDEBAR;

const isValid = (v) => v === 'sidebar' || v === 'modal' || v === 'full';

function readMode() {
    try {
        const v = localStorage.getItem(STORAGE_KEY);
        return isValid(v) ? v : DEFAULT_MODE;
    } catch {
        return DEFAULT_MODE;
    }
}

export default function useTaskLayoutMode() {
    const [mode, setModeState] = useState(readMode);

    useEffect(() => {
        // I7 fix: ignore FULL coming from a remote tab. The FULL mode causes a
        // navigate() to the detail page, and we don't want a layout switch in
        // tab A to drag tab B onto a different route. Cross-tab sync still
        // works for SIDEBAR/MODAL — only FULL is tab-local and must originate
        // from an explicit click in the same tab.
        const handler = (e) => {
            if (e.key !== STORAGE_KEY) return;
            const next = isValid(e.newValue) ? e.newValue : DEFAULT_MODE;
            if (next === TASK_LAYOUT_MODES.FULL) return;
            setModeState(next);
        };
        window.addEventListener('storage', handler);
        return () => window.removeEventListener('storage', handler);
    }, []);

    const setMode = useCallback((next) => {
        if (!isValid(next)) return;
        setModeState(next);
        try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
    }, []);

    return { mode, setMode };
}
